import React from 'react'
import { Box, Typography } from '@mui/material'
import { ClockIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../constants/colors'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { TESTIDS } from '../../../config/testids'
import { Button } from '../../../interaxDesignSystemComponents'
import { ActivityWithSession } from '../../../types/GeneralTypes'
import { getReminderActivityName } from '../../hooks/useActivityNotifications'
import { DefaultModal } from '../DefaultModal'

interface NextActivityReminderModalProps {
  activity: ActivityWithSession
  minutesBeforeStart: number
  onDismiss: () => void
  onNavigate: () => void
}

const CLOCK_ICON_SIZE_PX = 40
const REMINDER_MODAL_MAX_WIDTH = '440px'

/** The next activity is minutes away: one dismissible modal on top of whatever the participant is doing. */
export const NextActivityReminderModal: React.FC<NextActivityReminderModalProps> = ({
  activity,
  minutesBeforeStart,
  onDismiss,
  onNavigate
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const activityName = getReminderActivityName(activity, t)

  const goToActivities = () => {
    onNavigate()
    navigate(GamerRoutes.UPCOMING_ACTIVITIES)
  }

  return (
    <DefaultModal isOpen onClose={onDismiss} maxWidth={REMINDER_MODAL_MAX_WIDTH} showCloseButton={false}>
      <Box
        data-testid={TESTIDS.NEXT_ACTIVITY_REMINDER_MODAL}
        role="alertdialog"
        aria-live="assertive"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}
      >
        <ClockIcon
          style={{ width: CLOCK_ICON_SIZE_PX, height: CLOCK_ICON_SIZE_PX, color: INTERAX_COLORS.sun }}
          aria-hidden
        />
        <Typography
          sx={{ color: INTERAX_COLORS.white, fontWeight: 700, fontSize: { xs: '20px', sm: '22px', md: '24px' } }}
        >
          {t('gamer.notifications.nextActivityTitle', '{{activity}} starts in {{minutes}} min', {
            activity: activityName,
            minutes: minutesBeforeStart
          })}
        </Typography>
        <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: { xs: '14px', sm: '15px', md: '16px' } }}>
          {t(
            'gamer.notifications.nextActivityBody',
            'Wrap up what you are doing and go to your activities so you are ready when it starts.'
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
          <Button variant="secondary" onClick={onDismiss} data-testid={TESTIDS.NEXT_ACTIVITY_REMINDER_CLOSE_BTN}>
            {t('common.close', 'Close')}
          </Button>
          <Button variant="primary" onClick={goToActivities} data-testid={TESTIDS.NEXT_ACTIVITY_REMINDER_GO_BTN}>
            {t('gamer.notifications.goToActivities', 'Go to my activities')}
          </Button>
        </Box>
      </Box>
    </DefaultModal>
  )
}
